import React, { useEffect, useState, useMemo } from "react";
import { Table, Card, Col, Row, Statistic, Spin } from 'antd';
import { useAuth } from "react-oidc-context";
import { GetOnboardedCampaign } from "../services/ApiService";
import {
  GetCampaignStatus,
  GenerateCampaignDeskUrl,
} from "../services/UtilService";
import Alert from "react-bootstrap/Alert";
const CampaignListUI = () => {
  const [data, setData] = useState([]);
  const [numOfEndedCampaigns, setNumOfEndedCampaigns] = useState([]);
  const [numOfLiveCampaigns, setNumOfLiveCampaigns] = useState([]);
  const [numOfCountries, setNumOfCountries] = useState([]);
  const [numOfPartners, setNumOfPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const auth = useAuth();
  const [showError, setShowError] = useState(false);
  const columns = useMemo(
    () => [
      {
        dataIndex: "campaign_id",
        key: "cid",
        title: "Campaign Id",
        render: (text, record) => (
          <a
            href={GenerateCampaignDeskUrl(
              record.campaign_id,
              record.partner_id,
            )}
            target="_blank"
            rel="noopener noreferrer"
          >
            {record.campaign_id}
          </a>
        ),
      },
      {
        dataIndex: "advertiser_id",
        key: "aid",
        title: "Advertiser Id",
        sorter: (a, b) => a.advertiser_id.localeCompare(b.advertiser_id),
      },
      {
        dataIndex: "partner_id",
        key: "partner_id",
        title: "Partner Id",
        sorter: (a, b) => a.partner_id.localeCompare(b.partner_id),
      },
      {
        dataIndex: "channel",
        key: "channel",
        title: "Channel",
        sorter: (a, b) => a.channel.localeCompare(b.channel),
      },
      {
        dataIndex: "tccc_ou",
        key: "tccc_ou",
        title: "TCCC OU",
        sorter: (a, b) => a.tccc_ou.localeCompare(b.tccc_ou),
      },
      {
        dataIndex: "tccc_country",
        key: "tccc_country",
        title: "TCCC Country",
        sorter: (a, b) => a.tccc_country.localeCompare(b.tccc_country),
      },
      {
        dataIndex: "start_date",
        key: "start_date",
        title: "Start Date",
        sorter: (a, b) => a.start_date.localeCompare(b.start_date),
      },
      {
        dataIndex: "end_date",
        key: "end_date",
        title: "End Date",
        sorter: (a, b) => a.end_date.localeCompare(b.end_date),
      },
      {
        dataIndex: "campaign_status",
        key: "campaign_status",
        title: "Campaign Status",
        render: (text, record) => {
          const color = record.campaign_status_color;
          return <span style={{ color }}>{text}</span>;
        },
        sorter: (a, b) => a.campaign_status.localeCompare(b.campaign_status),
      },
      {
        dataIndex: "utc_timestamp_upload",
        key: "utc_timestamp_upload",
        title: "Uploaded Timestampe(UTC)",
        sorter: (a, b) =>
          a.utc_timestamp_upload.localeCompare(b.utc_timestamp_upload),
      },
      {
        dataIndex: "initial_bidlist_association",
        key: "initial_bidlist_association",
        title: "Bidlist Association(Initial)",
        sorter: (a, b) =>
          a.initial_bidlist_association.localeCompare(
            b.initial_bidlist_association,
          ),
      },
      {
        dataIndex: "latest_bidlist_association",
        key: "latest_bidlist_association",
        title: "Bidlist Association(Latest)",
        sorter: (a, b) =>
          a.latest_bidlist_association.localeCompare(
            b.latest_bidlist_association,
          ),
      },
    ],
    [],
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const raw_data = await GetOnboardedCampaign(auth.user.id_token);
        console.log(raw_data);

        const data = raw_data.map((campaign) => {
          const { status, color } = GetCampaignStatus(
            campaign.start_date,
            campaign.end_date,
          );
          return {
            ...campaign,
            campaign_status: status,
            campaign_status_color: color,
          };
        });
        const uniquePartnerIds = new Set(data.map(item => item.partner_id));
        const uniqueCountries = new Set(data.map(item => item.tccc_country));
        const endedCampaigns = data.filter(item => item.campaign_status==="Ended");
        const liveCampaigns = data.filter(item => item.campaign_status==="Live");

        setData(data);
        setLoading(false);
        setNumOfEndedCampaigns(endedCampaigns.length)
        setNumOfLiveCampaigns(liveCampaigns.length)
        setNumOfPartners(uniquePartnerIds.size)
        setNumOfCountries(uniqueCountries.size)

      } catch (err) {
        console.log(err.message);
        setError("Failed to retrieve the onboarded campaigns");
        setLoading(false);
        setShowError(true);
      }
    };
    fetchData();
  }, [auth.user.id_token]);

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}><Spin size="large" /></div>;

  return (
    <div className="table-container">
      {showError && (
        <Alert variant="danger" onClose={() => setShowError(false)} dismissible>
          <Alert.Heading>Error!</Alert.Heading>
          <p>{error}</p>
        </Alert>
      )}
      <Row gutter={20} justify="center">

        <Col span={4}>
          <Card>
          <div style={{ textAlign: 'center' }}>
             <Statistic title="Live Campaigns" valueStyle={{ color: '#3f8600' }} value={numOfLiveCampaigns} />
             </div>
          </Card>
        </Col>
        <Col span={4}>
          <Card>
          <div style={{ textAlign: 'center' }}>
             <Statistic title="Ended Campaigns" valueStyle={{ color: '#cf1322' }} value={numOfEndedCampaigns} />
             </div>
          </Card>
        </Col>
        <Col span={4}>
          <Card>
          <div style={{ textAlign: 'center' }}>
             <Statistic title="Number of Partners" value={numOfPartners} />
             </div>
          </Card>
        </Col>
        <Col span={4}>
          <Card>
          <div style={{ textAlign: 'center' }}>
             <Statistic title="Number of Countries" value={numOfCountries} />
             </div>
          </Card>
        </Col>

      </Row>
      <Table dataSource={data} columns={columns} rowKey="campaign_id" pagination={{ pageSize: 10, showSizeChanger: true }} />
    </div>
  );
};

export default CampaignListUI;
